import { B2Card } from "@hdb/biome-2";
import * as _ from "lodash";
import { createRef, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getUserMfaInfo } from "src/store/slices/user.slice";
import * as yup from "yup";
import MfaComponent from "../../../../../../core/components/mfa/mfa.component";
import ModalComponent from "../../../../../../core/components/modal/modal.component";
import { AssetCommandEnums } from "../../../../../../core/enums/assetCommands-enums";
import InfoTileContainer from "../../../../../../shared-elements/info-tile/info-tile-container.component";
import InfoTileComponent from "../../../../../../shared-elements/info-tile/info-tile.component";
import { safeAtob } from "../../../../../../shared/helpers/enode-decode-string";
import {
  getAssetByCode,
  getAssetLighteningParameters,
  getLightIntensity,
  getLightingOverride,
  getOperationModes,
  getSoftDimming,
  getTimeDelay,
  sendGetCommandToDms,
  setLightIntensity,
  setLightingOverride,
  setSoftDimming,
  setTimeDelay,
} from "../../../../../../store/slices/asset.slice";
import { setOtpIsValidation } from "../../../../../../store/slices/mfa-otp.slice";
import AssetActionFormComponent, {
  AssetActioFormConfig,
} from "./asset-action-form.component";
import "./asset-action-form.scss";

import { getUserInfo } from "src/core/auth/auth.service";
import ExecuteActionWithToaster from "src/shared/helpers/executeActionWithToaster";
import { displayToast } from "src/shared/helpers/toaster.helper";

const LightingParameterCommonComponent = () => {
  const lightingOverrideFormRef = createRef();
  const softDimmingFormRef = createRef();
  const lightIntensityFormRef = createRef();
  const timeDelayFormRef = createRef();
  const user = getUserInfo();

  const dispatch = useDispatch();
  const params: any = useParams();
  const id = params.id ? safeAtob(params.id) : null;

  const [operationModeOptions, setOperationModeOptions] = useState<any>([]);

  const [isLightingOverride, setIsLightingOverride] = useState<boolean>(false);
  // const [isSoftDimming, setIsSoftDimming] = useState<boolean>(false);
  const [isLightIntensity, setIsLightIntensity] = useState<boolean>(false);
  const [isTimeDelay, setIsTimeDelay] = useState<boolean>(false);

  useEffect(() => {
    dispatch(getOperationModes());
  }, [dispatch]);

  useEffect(() => {
    // const user = getUserInfo();
    dispatch(getUserMfaInfo());
  }, []);

  const mfaInfo = useSelector((state: any) => {
    return state.user.mfaInfo;
  });

  const assetDetails = useSelector((state: any) => {
    return state.asset.assetDetails;
  });

  const isLoadingAssetDetails = useSelector((state: any) => {
    return state.asset.isLoadingAssetDetails;
  });

  const operationModeResponse = useSelector((state: any) => {
    return state.asset.operationModes;
  });

  useEffect(() => {
    const modes = operationModeResponse?.data?.map((mode: any) => ({
      value: mode?.id,
      label: mode?.name,
    }));

    setOperationModeOptions(modes);
  }, [operationModeResponse]);

  const [
    lightingOverrideFormInitialValues,
    setLightingOverrideFormInitialValues,
  ] = useState({
    operationModeId: "",
    failSafeDelay: "",
  });

  const lightingOverrideRespose = useSelector((state: any) => {
    return state.asset.lightingOverrides;
  });

  const [isLoadingLightingOverrides, setIsLoadingLightingOverrides] =
    useState(false);

  useEffect(() => {
    setIsLoadingLightingOverrides(true);
    const data: any = {
      operationModeId: lightingOverrideRespose?.operationModeId,
      failSafeDelay: lightingOverrideRespose?.failsafeDelay,
    };

    setLightingOverrideFormInitialValues(data);
  }, [lightingOverrideRespose]);

  useEffect(() => {
    setIsLoadingLightingOverrides(false);
  }, [lightingOverrideFormInitialValues]);

  const onRefreshClickLightingOverride = () => {
    setLightingOverrideFormInitialValues({
      operationModeId: "",
      failSafeDelay: "",
    });

    dispatch(getLightingOverride({ id }));

    ExecuteActionWithToaster(
      dispatch,
      sendGetCommandToDms({
        assetCode: id,
        module: Number(AssetCommandEnums.getLightOverride),
      })
    );
  };

  const [isOTPModalOpen, setIsOtpModalOpen] = useState<boolean>(false);

  const [softDimmingFormInitialValues, setSoftDimmingFormInitialValues] =
    useState({
      rampUpTime: "",
      rampDownTime: "",
    });

  const softDimmingRespose = useSelector((state: any) => {
    return state.asset.softDimmings;
  });

  const [isLoadingSoftDimmings, setIsLoadingSoftDimmings] = useState(false);

  useEffect(() => {
    setIsLoadingSoftDimmings(true);
    const data: any = {
      rampUpTime: softDimmingRespose?.rampUpTime,
      rampDownTime: softDimmingRespose?.rampDownTime,
    };

    setSoftDimmingFormInitialValues(data);
  }, [softDimmingRespose]);

  useEffect(() => {
    setIsLoadingSoftDimmings(false);
  }, [softDimmingFormInitialValues]);

  const onRefreshClickSoftDimming = () => {
    setSoftDimmingFormInitialValues({
      rampUpTime: "",
      rampDownTime: "",
    });

    dispatch(getSoftDimming({ id }));

    ExecuteActionWithToaster(
      dispatch,
      sendGetCommandToDms({
        assetCode: id,
        module: Number(AssetCommandEnums.getSoftDimming),
      })
    );
  };

  const [lightIntensityFormInitialValues, setLightIntensityFormInitialValues] =
    useState({
      dimLevel: "",
      brightLevel: "",
    });

  const lightIntensityRespose = useSelector((state: any) => {
    return state.asset.lightIntensities;
  });

  const isOtpVerified = useSelector((state: any) => {
    return state.otp.isOtpVerified;
  });

  const socketRealTimeUpdate = useSelector((state: any) => {
    return state.asset.socketRealTimeUpdate;
  });

  const socketRealTimeUpdateData = useSelector((state: any) => {
    return state.asset.socketRealTimeUpdateData;
  });

  const socketData = useSelector((state: any) => {
    return state.asset.socketData;
  });

  const [isLoadingLightIntensities, setIsLoadingLightIntensities] =
    useState(false);

  useEffect(() => {
    setIsLoadingLightIntensities(true);
    const data: any = {
      dimLevel: lightIntensityRespose?.dimLevel,
      brightLevel: lightIntensityRespose?.brightLevel,
    };

    setLightIntensityFormInitialValues(data);
  }, [lightIntensityRespose]);

  useEffect(() => {
    setIsLoadingLightIntensities(false);
  }, [lightIntensityFormInitialValues]);

  const onRefreshClickLightIntensity = () => {
    setLightIntensityFormInitialValues({
      dimLevel: "",
      brightLevel: "",
    });

    dispatch(getLightIntensity({ id }));

    ExecuteActionWithToaster(
      dispatch,
      sendGetCommandToDms({
        assetCode: id,
        module: Number(AssetCommandEnums.getLightIntensity),
      })
    );
  };

  const [timeDelayFormInitialValues, setTimeDelayFormInitialValues] = useState({
    timeDelay: "",
  });

  const timeDelayRespose = useSelector((state: any) => {
    return state.asset.timeDelays;
  });

  const [isLoadingTimeDelays, setIsLoadingTimeDelays] = useState(false);

  useEffect(() => {
    setIsLoadingTimeDelays(true);
    const data: any = {
      timeDelay: timeDelayRespose?.timeDelay,
    };

    setTimeDelayFormInitialValues(data);
  }, [timeDelayRespose]);

  useEffect(() => {
    setIsLoadingTimeDelays(false);
  }, [timeDelayFormInitialValues]);

  const onRefreshClickTimeDelay = () => {
    setTimeDelayFormInitialValues({
      timeDelay: "",
    });

    dispatch(getTimeDelay({ id }));
    ExecuteActionWithToaster(
      dispatch,
      sendGetCommandToDms({
        assetCode: id,
        module: Number(AssetCommandEnums.getTimeDelay),
      })
    );
  };

  const onSubmitLightingOverride = () => {
    const form: any = lightingOverrideFormRef.current;
    if (form) {
      form.handleSubmit();
      const data: any = {
        assetCode: id,
        operationModeId: Number.parseInt(form.values.operationModeId),
        failSafeDelay: Number.parseInt(form.values.failSafeDelay),
        isAcknowledged: true,
      };

      if (
        data.failSafeDelay == null || // Checks for null or undefined
        Number.isNaN(data.failSafeDelay) || // Checks for NaN
        !data.operationModeId ||
        data.failSafeDelay > 250 ||
        data.failSafeDelay < 0
      ) {
        return;
      }

      setLightingOverrideFormInitialValues(data);

      if (mfaInfo?.isDMSMfaEnabled) {
        setIsOtpModalOpen(true);
      } else {
        // dispatch(setLightingOverride({ body: data }));
        ExecuteActionWithToaster(dispatch, setLightingOverride({ body: data }));
      }

      setIsLightingOverride(true);
      setIsTimeDelay(false);
      setIsLightIntensity(false);
      // setIsSoftDimming(false);
    }
  };

  const onSubmitSoftDimming = () => {
    const form: any = softDimmingFormRef.current;
    if (form) {
      form.handleSubmit();
      const data: any = {
        assetCode: id,
        rampUpTime: Number.parseInt(form.values.rampUpTime),
        rampDownTime: Number.parseInt(form.values.rampDownTime),
        isAcknowledged: true,
      };

      if (
        !data.rampUpTime ||
        !data.rampDownTime ||
        data.rampUpTime > 60 || // 3
        data.rampUpTime < 0 ||
        data.rampDownTime > 60 || // 3
        data.rampDownTime < 0
      ) {
        return;
      }

      setSoftDimmingFormInitialValues(data);

      if (mfaInfo.isDMSMfaEnabled) {
        setIsOtpModalOpen(true);
      } else {
        // dispatch(setSoftDimming({ body: data }));
        ExecuteActionWithToaster(dispatch, setSoftDimming({ body: data }));
      }

      setIsLightingOverride(false);
      setIsTimeDelay(false);
      setIsLightIntensity(false);
      // setIsSoftDimming(true);
    }
  };

  const onSubmitLightIntensity = () => {
    const form: any = lightIntensityFormRef.current;
    if (form) {
      form.handleSubmit();
      const data: any = {
        assetCode: id,
        dimLevel: Number.parseInt(form.values.dimLevel),
        brightLevel: Number.parseInt(form.values.brightLevel),
        isAcknowledged: true,
      };

      if (
        !data.dimLevel ||
        !data.brightLevel ||
        data.dimLevel < 0 ||
        data.dimLevel > 50 ||
        data.brightLevel < 0 ||
        data.brightLevel > 100
      ) {
        return;
      }
      setLightIntensityFormInitialValues(data);

      if (mfaInfo.isDMSMfaEnabled) {
        setIsOtpModalOpen(true);
      } else {
        // dispatch(setLightIntensity({ body: data }));
        ExecuteActionWithToaster(dispatch, setLightIntensity({ body: data }));
      }

      setIsLightingOverride(false);
      setIsTimeDelay(false);
      setIsLightIntensity(true);
      // setIsSoftDimming(false);
    }
  };

  const onSubmitTimeDelay = () => {
    const form: any = timeDelayFormRef.current;
    if (form) {
      form.handleSubmit();
      const data: any = {
        assetCode: id,
        timeDelay: Number.parseInt(form.values.timeDelay),
        isAcknowledged: true,
      };

      if (
        data.timeDelay == null ||
        Number.isNaN(data.timeDelay) ||
        data.timeDelay > 60 ||
        data.timeDelay < 0
      ) {
        return;
      }

      setTimeDelayFormInitialValues(data);

      if (mfaInfo.isDMSMfaEnabled) {
        setIsOtpModalOpen(true);
      } else {
        // dispatch(setTimeDelay({ body: data }));
        ExecuteActionWithToaster(dispatch, setTimeDelay({ body: data }));
      }

      setIsLightingOverride(false);
      setIsTimeDelay(true);
      setIsLightIntensity(false);
      // setIsSoftDimming(false);
    }
  };

  const onCancelClickLightingOverride = () => {
    const form: any = lightingOverrideFormRef.current;
    form.resetForm();
  };
  const onCancelClickSoftDimming = () => {
    const form: any = softDimmingFormRef.current;
    form.resetForm();
  };
  const onCancelClickLightIntensity = () => {
    const form: any = lightIntensityFormRef.current;
    form.resetForm();
  };
  const onCancelClickTimeDelay = () => {
    const form: any = timeDelayFormRef.current;
    form.resetForm();
  };

  const lightingOverrideActionConfig: any = [
    {
      label: "Cancel",
      event: onCancelClickLightingOverride,
      variant: "secondary",
      moduleName: "Light Parameter",
      actionName: "Edit-Light Parameter",
    },
    {
      label: "Save",
      event: onSubmitLightingOverride,
      variant: "primary",
      moduleName: "Light Parameter",
      actionName: "Edit-Light Parameter",
    },
  ];

  const softDimmingActionConfig: any = [
    {
      label: "Cancel",
      event: onCancelClickSoftDimming,
      variant: "secondary",
      moduleName: "Light Parameter",
      actionName: "Edit-Light Parameter",
    },
    {
      label: "Save",
      event: onSubmitSoftDimming,
      variant: "primary",
      moduleName: "Light Parameter",
      actionName: "Edit-Light Parameter",
    },
  ];
  const lightIntensityActionConfig: any = [
    {
      label: "Cancel",
      event: onCancelClickLightIntensity,
      variant: "secondary",
      moduleName: "Light Parameter",
      actionName: "Edit-Light Parameter",
    },
    {
      label: "Save",
      event: onSubmitLightIntensity,
      variant: "primary",
      moduleName: "Light Parameter",
      actionName: "Edit-LIght Parameter",
    },
  ];
  const timeDelayActionConfig: any = [
    {
      label: "Cancel",
      event: onCancelClickTimeDelay,
      variant: "secondary",
      moduleName: "Light Parameter",
      actionName: "Edit-Light Parameter",
    },
    {
      label: "Save",
      event: onSubmitTimeDelay,
      variant: "primary",
      moduleName: "Light Parameter",
      actionName: "Edit-Light Parameter",
    },
  ];

  const lightingOverrideRefeshActionConfig: any = [
    {
      label: "Refesh",
      event: onRefreshClickLightingOverride,
      variant: "default",
    },
  ];

  const softDimmingRefeshActionConfig: any = [
    {
      label: "Refesh",
      event: onRefreshClickSoftDimming,
      variant: "default",
    },
  ];

  const lightIntensityRefeshActionConfig: any = [
    {
      label: "Refesh",
      event: onRefreshClickLightIntensity,
      variant: "default",
    },
  ];

  const TimeDelayRefeshActionConfig: any = [
    {
      label: "Refesh",
      event: onRefreshClickTimeDelay,
      variant: "default",
    },
  ];

  const lightingOverrideFormConfig: AssetActioFormConfig = {
    controls: [
      {
        label: "Lighting Override",
        isGrid: true,
        controlGroup: [
          {
            name: "operationModeId",
            label: "Operation Mode",
            placeholder: "Select  Lighting Operation Mode",
            type: "select",
            options: operationModeOptions,
            required: true,
          },
          {
            name: "failSafeDelay",
            label: "Failsafe Delay",
            placeholder: "e.g: 200",
            type: "number",
            required: true,
            suffix: "min",
          },
        ],
      },
    ],
  };

  const lightingOverrideFormValidationSchema = yup.object().shape({
    operationModeId: yup.string().required("Operation mode ID is required"),
    failSafeDelay: yup
      .number()
      .required("Fail safe delay is required")
      .min(0, "Fail safe delay must be greater than or equal to 0")
      .max(250, "Fail safe delay must be less than or equal to 250"),
  });

  const softDimmingFormConfig: AssetActioFormConfig = {
    controls: [
      {
        label: "Soft Dimming",
        isGrid: true,
        controlGroup: [
          {
            name: "rampUpTime",
            label: "Ramp Up Time",
            placeholder: "e.g: 2",
            type: "number",
            suffix: "sec",
            required: true,
          },
          {
            name: "rampDownTime",
            label: "Ramp Down Time",
            placeholder: "e.g: 2",
            type: "number",
            suffix: "sec",
            required: true,
          },
        ],
      },
    ],
  };

  const softDimmingFormValidationSchema = yup.object().shape({
    rampUpTime: yup
      .number()
      .required("Ramp up time is required")
      .min(1, "Ramp up time must be greater than or equal to 1")
      .max(60, "Ramp up time must be less than or equal to 60"), // 3
    rampDownTime: yup
      .number()
      .required("Ramp down time is required")
      .min(1, "Ramp down time must be greater than or equal to 1")
      .max(60, "Ramp down time must be less than or equal to 60"),
  });

  const lightIntensityFormConfig: AssetActioFormConfig = {
    controls: [
      {
        label: "Light Intensity",
        isGrid: true,
        controlGroup: [
          {
            name: "dimLevel",
            label: "Dim Intensity",
            placeholder: "e.g: 40",
            type: "number",
            suffix: "%",
            required: true,
          },
          {
            name: "brightLevel",
            label: "Bright Intensity",
            placeholder: "e.g: 80",
            type: "number",
            suffix: "%",
            required: true,
          },
        ],
      },
    ],
  };

  const lightIntensityFormValidationSchema = yup.object().shape({
    dimLevel: yup
      .number()
      .required("Dim Intensity is required")
      .min(0, "Dim Intensity must be greater than or equal to 0")
      .max(50, "Dim Intensity must be less than or equal to 50"),
    brightLevel: yup
      .number()
      .required("Bright Intensity is required")
      .min(0, "Bright Intensity must be greater than or equal to 0") // 50
      .max(100, "Bright Intensity must be less than or equal to 100"),
  });

  const timeDelayFormConfig: AssetActioFormConfig = {
    controls: [
      {
        label: "Time Delay",
        isGrid: true,
        controlGroup: [
          {
            name: "timeDelay",
            label: "Hold Time",
            placeholder: "e.g: 60",
            type: "number",
            suffix: "sec",
            required: true,
          },
        ],
      },
    ],
  };

  const timeDelayFormValidationSchema = yup.object().shape({
    timeDelay: yup
      .number()
      .required("Time delay is required")
      .min(0, "Time delay must be greater than or equal to 0")
      .max(60, "Time delay must be less than or equal to 60"),
  });

  useEffect(() => {
    if (!id) return;
    dispatch(getAssetByCode({ id }));
    dispatch(getAssetLighteningParameters({ id }));
    dispatch(getLightingOverride({ id }));
    dispatch(getSoftDimming({ id }));
    dispatch(getLightIntensity({ id }));
    dispatch(getTimeDelay({ id }));
  }, [id]);

  // useEffect(() => {
  //   if (
  //     lighteningParameters &&
  //     Object.keys(lighteningParameters).length &&
  //     typeof lighteningParameters === "object"
  //   ) {
  //     const { brightLevel, dimLevel } =
  //       lighteningParameters.lightIntensityModel;
  //     const { failSafeDelay, operationModeId } =
  //       lighteningParameters.lightingOverride;
  //     const { rampDownTime, rampUpTime } =
  //       lighteningParameters.softDimmingModel;
  //     const { timeDelay } = lighteningParameters.timeDelayModel;

  //     const data = {
  //       brightLevel,
  //       dimLevel,
  //       failSafeDelay,
  //       operationModeId,
  //       rampDownTime,
  //       rampUpTime,
  //       timeDelay,
  //     };

  //     setFomInitialValues(data);
  //   }
  // }, [lighteningParameters]);

  const showToaster = (socketRealTimeUpdateData: any) => {

    if (
      user[
      "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
      ] === socketData?.NotificationIdentifier
    ) {
      displayToast(
        socketRealTimeUpdateData?.PushMessage || "Acknowledgement Received",
        socketRealTimeUpdateData?.ToasterColor
      );
    }
  };

  useEffect(() => {
    const copiedSocketRealTimeUpdate = _.cloneDeep(socketRealTimeUpdate);

    if (copiedSocketRealTimeUpdate.LightingOverride) {
      showToaster(socketRealTimeUpdateData);
      dispatch(getLightingOverride({ id }));
    } else if (copiedSocketRealTimeUpdate.SoftDimming) {
      showToaster(socketRealTimeUpdateData);
      dispatch(getSoftDimming({ id }));
    } else if (copiedSocketRealTimeUpdate.LightIntensity) {
      showToaster(socketRealTimeUpdateData);
      dispatch(getLightIntensity({ id }));
    } else if (copiedSocketRealTimeUpdate.TimeDelay) {
      showToaster(socketRealTimeUpdateData);
      dispatch(getTimeDelay({ id }));
    }
  }, [socketRealTimeUpdate]); // socketRealTimeUpdateData

  const handleCloseModal = () => {
    setIsOtpModalOpen(false);
  };

  const onSubmitMfaOtp = () => {
    if (isLightingOverride) {
      ExecuteActionWithToaster(
        dispatch,
        setLightingOverride({ body: lightingOverrideFormInitialValues })
      ).then(() => {
        setIsOtpModalOpen(false);
      });
    } else if (isLightIntensity) {
      ExecuteActionWithToaster(
        dispatch,
        setLightIntensity({ body: lightIntensityFormInitialValues })
      ).then(() => {
        setIsOtpModalOpen(false);
      });
    } else if (isTimeDelay) {
      ExecuteActionWithToaster(
        dispatch,
        setTimeDelay({ body: timeDelayFormInitialValues })
      ).then(() => {
        setIsOtpModalOpen(false);
      });
    } else {
      ExecuteActionWithToaster(
        dispatch,
        setSoftDimming({ body: softDimmingFormInitialValues })
      ).then(() => {
        setIsOtpModalOpen(false);
      });
    }
    setIsOtpModalOpen(false);
    dispatch(setOtpIsValidation(false));
  };

  const mfaActionConfig: any = [
    {
      label: "Save",
      event: onSubmitMfaOtp,
      variant: "primary",
      disabled: !isOtpVerified,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Lighting Parameters | Asset</title>
      </Helmet>
      <div>
        <div className="asset-details">
          <InfoTileContainer header="Asset Details">
            <InfoTileComponent
              title="Asset ID"
              value={assetDetails?.assetId}
              valueWidth={3}
              isLoading={isLoadingAssetDetails}
            />
            <InfoTileComponent
              title="Display Name"
              value={assetDetails?.sensorDisplayName}
              valueWidth={3}
              isLoading={isLoadingAssetDetails}
            />
            <InfoTileComponent
              title="Asset Type"
              value={
                assetDetails?.assetType == "Luminarie"
                  ? "Luminaire"
                  : assetDetails?.assetType
              }
              isLoading={isLoadingAssetDetails}
            />
          </InfoTileContainer>

          <InfoTileContainer header="Location Details">
            <InfoTileComponent
              title="Town Council"
              value={assetDetails?.townCouncil}
              isLoading={isLoadingAssetDetails}
            />
            <InfoTileComponent
              title="Address"
              value={assetDetails?.address}
              isLoading={isLoadingAssetDetails}
            />
            <InfoTileComponent
              title="Level"
              value={assetDetails?.floor}
              isLoading={isLoadingAssetDetails}
            />
          </InfoTileContainer>
        </div>
        <div className="asset-conf-wrapper mt-4">
          <B2Card className="card-bg">
            <AssetActionFormComponent
              formRef={lightingOverrideFormRef}
              initialValues={lightingOverrideFormInitialValues}
              formConfig={lightingOverrideFormConfig}
              actionConfig={lightingOverrideActionConfig}
              refreshActionConfig={lightingOverrideRefeshActionConfig}
              isLoadingData={isLoadingLightingOverrides}
              validation={lightingOverrideFormValidationSchema}
              moduleName={"Light Parameter"}
              actionName={"Edit-Light Parameter"}
            />
          </B2Card>
          <B2Card className="card-bg">
            <AssetActionFormComponent
              formRef={softDimmingFormRef}
              initialValues={softDimmingFormInitialValues}
              formConfig={softDimmingFormConfig}
              actionConfig={softDimmingActionConfig}
              refreshActionConfig={softDimmingRefeshActionConfig}
              isLoadingData={isLoadingSoftDimmings}
              validation={softDimmingFormValidationSchema}
              moduleName={"Light Parameter"}
              actionName={"Edit-Light Parameter"}
            />
          </B2Card>
          {assetDetails?.assetType == "Luminarie" && (
            <B2Card className="card-bg">
              <AssetActionFormComponent
                formRef={lightIntensityFormRef}
                initialValues={lightIntensityFormInitialValues}
                formConfig={lightIntensityFormConfig}
                actionConfig={lightIntensityActionConfig}
                refreshActionConfig={lightIntensityRefeshActionConfig}
                isLoadingData={isLoadingLightIntensities}
                validation={lightIntensityFormValidationSchema}
                moduleName={"Light Parameter"}
                actionName={"Edit-Light Parameter"}
              />
            </B2Card>
          )}

          <B2Card className="card-bg">
            <AssetActionFormComponent
              formRef={timeDelayFormRef}
              initialValues={timeDelayFormInitialValues}
              formConfig={timeDelayFormConfig}
              actionConfig={timeDelayActionConfig}
              refreshActionConfig={TimeDelayRefeshActionConfig}
              isLoadingData={isLoadingTimeDelays}
              validation={timeDelayFormValidationSchema}
              moduleName={"Light Parameter"}
              actionName={"Edit-Light Parameter"}
            />
          </B2Card>
          {isOTPModalOpen && (
            <ModalComponent
              showModal={isOTPModalOpen}
              handleClose={handleCloseModal}
              actionConfig={mfaActionConfig}
              headerTitle={"Multi Factor Authentication"}
              isViewMode={false}
            >
              <MfaComponent />
            </ModalComponent>
          )}
        </div>
      </div>
    </>
  );
};

export default LightingParameterCommonComponent;
